import { z } from 'zod';

const decimalString = z.string().regex(/^-?\d+(\.\d{1,4})?$/);

export const PnlRowSchema = z.object({
  accountId: z.string().uuid(),
  accountCode: z.string(),
  accountName: z.string(),
  accountType: z.enum(['revenue', 'expense']),
  baseAmount: decimalString,
});
export type PnlRow = z.infer<typeof PnlRowSchema>;

export const PnlReportSchema = z.object({
  fromDate: z.string(),
  toDate: z.string(),
  baseCurrencyCode: z.string().length(3),
  revenue: z.array(PnlRowSchema),
  expenses: z.array(PnlRowSchema),
  totalRevenue: decimalString,
  totalExpenses: decimalString,
  netIncome: decimalString,
});
export type PnlReport = z.infer<typeof PnlReportSchema>;

export const BalanceSheetRowSchema = z.object({
  accountId: z.string().uuid(),
  accountCode: z.string(),
  accountName: z.string(),
  accountType: z.enum(['asset', 'liability', 'equity']),
  baseBalance: decimalString,
});
export type BalanceSheetRow = z.infer<typeof BalanceSheetRowSchema>;

export const BalanceSheetSchema = z.object({
  asOf: z.string(),
  baseCurrencyCode: z.string().length(3),
  assets: z.array(BalanceSheetRowSchema),
  liabilities: z.array(BalanceSheetRowSchema),
  equity: z.array(BalanceSheetRowSchema),
  totalAssets: decimalString,
  totalLiabilities: decimalString,
  totalEquity: decimalString,
});
export type BalanceSheet = z.infer<typeof BalanceSheetSchema>;

// ---- Aging (AR / AP) ----
export const AgingBucketSchema = z.enum(['current', 'days_1_30', 'days_31_60', 'days_61_90', 'days_90_plus']);
export type AgingBucket = z.infer<typeof AgingBucketSchema>;

export const AgingPartyRowSchema = z.object({
  partyId: z.string().uuid(),
  partyCode: z.string(),
  partyName: z.string(),
  /** Currency of this row's amounts — a party has one row per open-document currency. */
  currencyCode: z.string().length(3),
  current: z.string(),
  days_1_30: z.string(),
  days_31_60: z.string(),
  days_61_90: z.string(),
  days_90_plus: z.string(),
  total: z.string(),
});
export type AgingPartyRow = z.infer<typeof AgingPartyRowSchema>;

export const AgingTotalsSchema = z.object({
  current: z.string(),
  days_1_30: z.string(),
  days_31_60: z.string(),
  days_61_90: z.string(),
  days_90_plus: z.string(),
  total: z.string(),
});
export type AgingTotals = z.infer<typeof AgingTotalsSchema>;

export const AgingReportSchema = z.object({
  asOf: z.string(),
  baseCurrencyCode: z.string().length(3),
  rows: z.array(AgingPartyRowSchema),
  /**
   * DEPRECATED: raw sum across ALL currencies (kept so existing readers keep
   * working). Use `totalsByCurrency` — mixing currencies in one figure is
   * meaningless when a tenant has open documents in more than one currency.
   */
  totals: AgingTotalsSchema,
  /** Grand totals per document currency (one entry per currency present in `rows`). */
  totalsByCurrency: z.array(AgingTotalsSchema.extend({ currencyCode: z.string().length(3) })),
});
export type AgingReport = z.infer<typeof AgingReportSchema>;

// ---- Dashboard overview (operational "today" landing) ----
/** One "today" tally — count of documents + their base-currency total. */
export const DashboardTodaySchema = z.object({ count: z.number(), total: decimalString }).nullable();
export type DashboardToday = z.infer<typeof DashboardTodaySchema>;

/** A most-recent document reference (invoice / order). */
export const DashboardDocRefSchema = z.object({
  id: z.string().uuid(),
  docNumber: z.string(),
  docDate: z.string(),
  status: z.string(),
  total: z.string(),
  currencyCode: z.string(),
}).nullable();
export type DashboardDocRef = z.infer<typeof DashboardDocRefSchema>;

export const DashboardOverviewSchema = z.object({
  baseCurrencyCode: z.string().length(3),
  /** Per-channel "today" tallies; a section is null when the caller lacks its read permission. */
  today: z.object({
    invoices: DashboardTodaySchema,
    orders: DashboardTodaySchema,
    pos: DashboardTodaySchema,
    /** Inbound payments (money collected) posted today. */
    payments: DashboardTodaySchema,
    /** Customers created today (null without parties.read). */
    newCustomers: z.number().nullable(),
    /** Units sold at POS today (null without pos.read). */
    unitsSold: z.number().nullable(),
    /**
     * Money going OUT today - posted expenses, base currency. Every other tally
     * here is money coming IN, so without this the board can say what was sold
     * but not whether the day made anything.
     *
     * Gated on `gl.read`: expenses have no permission of their own in this
     * system, they ride on the ledger's. Optional so an older API omits the tile
     * rather than rendering a zero that looks like a fact.
     */
    expenses: DashboardTodaySchema.optional(),
  }),
  /**
   * Today's revenue minus today's expenses, base currency. Null unless the
   * caller can see BOTH halves - half a subtraction reads as a profit that
   * quietly ignores the costs.
   */
  netToday: z.string().nullable().optional(),
  /**
   * How much of the shop there IS. NOT branch-scoped, deliberately: a product
   * exists company-wide, so a per-branch product count is a number with no
   * meaning. Each entry is null without its own permission.
   */
  catalogue: z.object({
    products: z.number().nullable(),
    brands: z.number().nullable(),
    categories: z.number().nullable(),
    customers: z.number().nullable(),
    suppliers: z.number().nullable(),
    warehouses: z.number().nullable(),
  }).optional(),
  /**
   * What actually moved today, merged and newest first: invoices, POS sales,
   * money in, money out.
   *
   * Distinct from `lastActivity`, which reads the audit log and reports that
   * somebody EDITED something - useful, but not the same question as what moved
   * today. Each source contributes only if the caller may read it.
   */
  todayTransactions: z.array(z.object({
    id: z.string(),
    kind: z.enum(['invoice', 'pos', 'payment', 'expense']),
    reference: z.string(),
    /** Customer, or the payee an expense was paid to. Null when there is none. */
    party: z.string().nullable(),
    /** Positive for money in, NEGATIVE for money out, so it reads as a ledger. */
    amount: z.string(),
    currencyCode: z.string(),
    at: z.string(),
  })).optional(),
  /** Base-currency sales per day for the last 7 days (POS + non-POS invoices); [] without pos/invoices read. */
  salesTrend: z.array(z.object({ date: z.string(), total: z.string() })),
  /** Yesterday's base-currency revenue (same POS+invoices basis as today's revenue tile); null without pos/invoices read. Powers the today-vs-yesterday delta. */
  revenueYesterday: z.string().nullable(),
  /** Tracked items running low (total stock ≤ threshold), lowest first; [] without stock.read. */
  lowStock: z.array(z.object({
    itemId: z.string().uuid(),
    name: z.string(),
    code: z.string(),
    quantity: z.number(),
  })),
  lastInvoice: DashboardDocRefSchema,
  lastOrder: DashboardDocRefSchema,
  lastProduct: z.object({
    id: z.string().uuid(),
    name: z.string(),
    code: z.string(),
    createdAt: z.string(),
  }).nullable(),
  /** The newest few products (head = lastProduct) — "what came in lately" is
   *  a list question, and the dashboard shows it as one. Optional: an older
   *  API omits it and the card simply doesn't render. */
  newestProducts: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    code: z.string(),
    createdAt: z.string(),
  })).optional(),
  lastActivity: z.array(z.object({
    id: z.string(),
    actorName: z.string().nullable(),
    operation: z.string().nullable(),
    action: z.string(),
    entityType: z.string(),
    at: z.string(),
  })),
  /** Top-selling products by units sold (POS finalized + online sales orders). */
  topSellers: z.array(z.object({
    itemId: z.string().uuid(),
    name: z.string(),
    code: z.string(),
    qtySold: z.number(),
  })),
  /** Biggest debtors by open AR in the base currency. */
  topDebtors: z.array(z.object({
    partyId: z.string().uuid(),
    partyName: z.string(),
    currencyCode: z.string(),
    total: z.string(),
  })),
  /** Most recent online (storefront) orders — the dashboard's new-orders feed. `createdAt` is UTC ISO (format in Iraq time client-side). [] without cms.read/sales_orders.read. */
  recentOnlineOrders: z.array(z.object({
    id: z.string().uuid(),
    orderNumber: z.string(),
    customerName: z.string().nullable(),
    total: z.string(),
    currencyCode: z.string(),
    status: z.string(),
    source: z.enum(['app', 'website']),
    version: z.string().nullable(),
    createdAt: z.string(),
  })),
});
export type DashboardOverview = z.infer<typeof DashboardOverviewSchema>;
